import React from 'react';

import {getMonth, getYear} from 'date-fns';
import i18n from 'i18next';
import {ReactDatePickerProps} from 'react-datepicker';
import {useTranslation} from 'react-i18next';

import 'react-datepicker/dist/react-datepicker.css';
import {MONTH_KEYS} from '@consts/date';
import {Icon} from '@shared/ui/Icon';
import {Select, SelectOption} from '@shared/ui/Select';
import {yearsOptions} from '@utils/select';

import {DatePickerInput} from './input';
import {DatePickerArrow, DatePickerHead, InnerDatePicker} from './styled';

export type DatePickerProps = Omit<ReactDatePickerProps, 'onChange'> & {
    onChange?: (date: Date | null, event: React.SyntheticEvent<any> | undefined) => void;
    value?: Date | null;
    label?: string;
    hasError?: boolean;
    formFieldName?: string;
    isRequired?: boolean;
    size?: 'sm';
};

export const DatePicker = ({
    onChange = () => {},
    hasError,
    customInput,
    className,
    label,
    value,
    size,
    isRequired = false,
    ...other
}: DatePickerProps) => {
    const {t} = useTranslation();
    const options = yearsOptions();
    const months = MONTH_KEYS.map((key, i) => ({
        label: t(`dictionaries.months.${key}.short`),
        value: i,
    }));

    const renderCustomHeader = ({
        date,
        decreaseMonth,
        increaseMonth,
        changeMonth,
        changeYear,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
    }: Parameters<NonNullable<ReactDatePickerProps['renderCustomHeader']>>[0]) => (
        <DatePickerHead month={other.showMonthYearPicker}>
            <DatePickerArrow
                type="button"
                onClick={decreaseMonth}
                disabled={prevMonthButtonDisabled}
            >
                <Icon name="arrowLeft" size={14} />
            </DatePickerArrow>
            {!other.showMonthYearPicker && (
                <Select
                    options={months}
                    value={months.find((o) => o.value === getMonth(date))}
                    onChange={(v: SelectOption<number>) => {
                        if (v.value !== undefined && v.value !== null) changeMonth(v.value);
                    }}
                />
            )}
            <Select
                options={options}
                value={options.find((o) => o.value === getYear(date))}
                onChange={(v: SelectOption<number>) => v.value && changeYear(v.value)}
            />
            <DatePickerArrow
                type="button"
                onClick={increaseMonth}
                disabled={nextMonthButtonDisabled}
                right
            >
                <Icon name="arrowLeft" size={14} />
            </DatePickerArrow>
        </DatePickerHead>
    );

    return (
        <InnerDatePicker
            selected={value}
            renderCustomHeader={renderCustomHeader}
            onChange={onChange}
            popperClassName={className}
            showPopperArrow={false}
            dateFormat="dd.MM.yyyy"
            autoComplete="off"
            locale={i18n.language}
            customInput={
                React.isValidElement(customInput) ? (
                    <customInput.type {...customInput.props} label={label} hasError={hasError} />
                ) : (
                    <DatePickerInput
                        size={size}
                        isRequired={isRequired}
                        label={label}
                        hasError={hasError}
                        placeholder={t('components.datepicker.SelectDate')}
                    />
                )
            }
            {...other}
        />
    );
};

export {globalStyles as globalDatePickerStyles} from './styled';
